import {
  Alert,
  ArrowRightIcon,
  Box,
  Button,
  CheckCircle2Icon,
  Divider,
  Drawer,
  DrawerBody,
  Link,
  RefreshIcon,
  Text,
  UserIcon,
} from '@razorpay/blade/components';
import { ModalActionButtons } from 'components/SalaryComponents/components/ModalActionButtons';
import {
  DetailsContainer,
  DetailsFooter,
  DetailsHeader,
  DetailsSection,
  DetailsSectionHeading,
  DetailsSectionItem,
  DetailsSectionItemsContainer,
  DetailsViewSkeleton,
} from 'components/SalaryComponents/components/details/DetailsViewHelpers';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { useQueryClient } from 'react-query';
import { salaryComponentParams } from '../../constants';

import { PerquisitesDummyData } from 'components/SalaryComponents/__tests__/data';
import { PerquisitesComponentType } from 'components/SalaryComponents/types';
import React, { useEffect, useState } from 'react';
import { EmptyView } from '../../components/EmptyView';

const PerquisitesDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openPredefinedEditPage } = useSalaryComponentHelpers();
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isOpen = !!salaryComponentId;

  // Mock data state
  const [data, setData] = useState<PerquisitesComponentType | null>(null);

  // mock loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Temporarily using this to mock data
  useEffect(() => {
    if (salaryComponentId) {
      const foundComponent = PerquisitesDummyData.find(
        (component) => component.id === salaryComponentId,
      );

      setData(foundComponent || null);
      setIsError(!foundComponent);
    } else {
      setData(null);
    }
  }, [salaryComponentId]);

  const refetch = () => {
    setIsLoading(true);

    // Simulate API delay
    setTimeout(() => {
      const foundComponent = PerquisitesDummyData.find(
        (component) => component.id === salaryComponentId,
      );

      setData(foundComponent || null);
      setIsLoading(false);
      setIsError(!foundComponent);
    }, 500);
  };

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  /* Commented out actual API call for future use
  const { data, isLoading, refetch, isError } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery],
    queryFn: () =>
      api.salaryComponents.queries.getSalaryComponent<PerquisitesComponentType>({
        id: salaryComponentId!,
        category: 'PERQUISITES_CATEGORY',
      }),
    enabled: isOpen,
    select(data) {
      return data.payrollGetSalaryComponent.data.salaryComponent;
    },
    onError(error) {
      console.log(error);
    },
    cacheTime: 0,
  });
  */

  const onDismiss = () => {
    // Commenting out API invalidation since we're using mock data
    /* 
    queryClient.invalidateQueries({
      queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery, salaryComponentId],
    });
    */
    closeModal();
    closeDetailsView();
  };

  const { displayBehaviour } = data?.settings?.behaviour || {};

  const handleEditClick = () => {
    if (data?.isPredefined) {
      openPredefinedEditPage(salaryComponentId!);
    } else {
      // TODO: Implement edit for perquisites when available
      console.log('Edit perquisite component', salaryComponentId);
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
        <DrawerBody>
          <DetailsContainer onDismiss={onDismiss}>
            {isLoading && <DetailsViewSkeleton onDismiss={onDismiss} />}
            {isError && (
              <EmptyView
                description={
                  <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                    Due to an unknown error, we could not load the component details page.
                  </Text>
                }>
                <Box display={'flex'} justifyContent={'center'}>
                  <Box width={'104px'}>
                    <Button icon={RefreshIcon} size={'small'} onClick={() => refetch()}>
                      Retry
                    </Button>
                  </Box>
                </Box>
              </EmptyView>
            )}
            {data ? (
              <>
                <DetailsHeader
                  heading={data.name}
                  description={data?.settings?.behaviour?.displayBehaviour?.description}
                  isActive={Boolean(data.isActive)}
                  isPredefined={Boolean(data.isPredefined)}
                />

                <Divider marginY={'spacing.7'} />

                {/* General section */}
                <DetailsSection>
                  <Alert
                    color={'information'}
                    icon={UserIcon}
                    description={
                      <>
                        <Box display={'flex'} flexDirection={'row'} gap={'spacing.2'}>
                          <Text size={'medium'} color={'surface.text.gray.subtle'}>
                            Component used by
                          </Text>
                          <Text weight={'medium'}>20 active employee</Text>
                        </Box>
                        <Link
                          icon={ArrowRightIcon}
                          iconPosition={'right'}
                          size={'small'}
                          onClick={() => {
                            console.log('open salary structure list section');
                          }}>
                          Details
                        </Link>
                      </>
                    }
                    isDismissible={false}
                  />

                  <DetailsSectionHeading
                    icon={<CheckCircle2Icon size={'large'} color={'surface.icon.gray.subtle'} />}
                    label="General"
                  />
                  <DetailsSectionItemsContainer>
                    <DetailsSectionItem label="Component Name" value={data.name || ''} />
                    <DetailsSectionItem
                      label="Display name"
                      value={displayBehaviour?.displayName || ''}
                    />
                  </DetailsSectionItemsContainer>
                </DetailsSection>
              </>
            ) : null}
          </DetailsContainer>
          {/* Actions */}
          <DetailsFooter isSticky={true}>
            <ModalActionButtons
              isActive={Boolean(data?.isActive)}
              showEnableDisableButton={!Boolean(data?.isPredefined)}
              openModal={openModal}
              onEditClick={handleEditClick}
            />
          </DetailsFooter>
        </DrawerBody>
      </Drawer>
      {data ? (
        <ActionModals
          salaryComponent={data}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, you can add the component to an active employee's compensation
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once disabled, it cannot be used in an employee's compensation
                </Text>
              </Box>
            </Box>
          }
        />
      ) : null}
    </>
  );
};

export default PerquisitesDetailView;
